import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useUserContext } from 'common/contexts/UserContext';
import { ContainerUsage } from 'components/ContainerTracking';

import {
  assignCustomersToUpload,
  getUploadStatus,
  postWatchListAdd,
  uploadFileToWatchList,
} from 'services/clients/watchList';
import { TrackContainers } from '../types';
import { BlueCargoProperty, UploadedFileColumn } from './stages/customer-assignment/column-mapping';
import { Containers } from './stages/containers/Containers';
import { Assignment, CustomerAssignment } from './stages/customer-assignment';
import { Duplicate, Error, Success, Uploading } from './stages';

interface Props {
  onTrackAndTrace: (containers?: TrackContainers) => void;
  prepopulatedContainers?: string[];
}

interface StartStage {
  stage: 'start';
  acceptedFiles: File[];
  inputContainerNumbers: string[];
  /** For uploading manually entered container numbers */
  uploading: boolean;
}

interface UploadStage {
  stage: 'uploading';
  uploadId: string | null;
  columns: UploadedFileColumn[];
  fileName: string | null;
  /** Whether the job status was success */
  success: boolean;
}

interface DuplicateStage {
  stage: 'duplicate';
}

interface AssignmentStage {
  stage: 'assignment';
  assignment: Assignment;
  uploadId: string | null;
  columns: UploadedFileColumn[];
  fileName: string;
  /** Whether the job status was success */
  success: boolean;
}

interface CompletionStage {
  stage: 'complete';
}

interface ErrorStage {
  stage: 'error';
  message?: string;
  originStage: 'uploading' | 'assignment';
}

type Stage =
  | StartStage
  | UploadStage
  | DuplicateStage
  | AssignmentStage
  | CompletionStage
  | ErrorStage;

export const AddForm: React.FC<Props> = (props) => {
  const { onTrackAndTrace, prepopulatedContainers } = props;

  const queryClient = useQueryClient();

  /**
   * This component sends the user through a flow, so we keep track of the state or each step
   * here. This avoids us having extra confusing state at all times that isn't relevent for a
   * given stage
   */
  const [state, setState] = React.useState<Stage>({
    stage: 'start',
    acceptedFiles: [],
    inputContainerNumbers: prepopulatedContainers || [],
    uploading: false,
  });

  /** The upload ID is derived from our staged state */
  const uploadId = React.useMemo(
    () => (state.stage === 'uploading' || state.stage === 'assignment' ? state.uploadId : null),
    [state]
  );

  /** The upload method is derived from our staged state */
  const method: TrackContainers['method'] | null = React.useMemo(() => {
    if (
      state.stage !== 'start' ||
      (state.acceptedFiles.length === 0 && state.inputContainerNumbers.length === 0)
    ) {
      return null;
    }

    return state.inputContainerNumbers.length > 0 ? 'input' : 'upload';
  }, [state]);

  const { fetchUser } = useUserContext();

  /** Reset the state so we can try uploading again */
  const handleOnRetryUpload = React.useCallback(() => {
    setState({
      stage: 'start',
      acceptedFiles: [],
      inputContainerNumbers: [],
      uploading: false,
    });
  }, [setState]);

  /** Upload a list of container number strings */
  const uploadInputContainerNumbers = React.useCallback(
    async (numbers: string[]) => {
      if (state.stage !== 'start' || method === null) {
        return;
      }

      setState((s) => ({
        ...s,
        uploading: true,
      }));
      try {
        const data = await postWatchListAdd(numbers);

        if (data.code === 'ok') {
          if (data.noCapacityContainer) {
            fetchUser();
          }
          setState({ stage: 'complete' });
        } else if (data.code === 'alreadyExists') {
          setState({ stage: 'duplicate' });
        }
        onTrackAndTrace({ method, numbers });
      } catch (error) {
        setState({ stage: 'error', originStage: 'uploading' });
      }
    },
    [fetchUser, setState, onTrackAndTrace, state, method]
  );

  /** Upload a list of container number files */
  const uploadFileContainerNumbers = React.useCallback(
    async (files: File[]) => {
      if (state.stage !== 'start' || method === null) {
        return;
      }

      const file = files[0];
      setState({
        stage: 'uploading',
        uploadId: null,
        columns: [],
        fileName: file.name,
        success: false,
      });
      try {
        const response = await uploadFileToWatchList(file);
        if (response.status === 'duplicate') {
          setState({ stage: 'duplicate' });
          return;
        }

        const columns: UploadedFileColumn[] = [];
        const {
          firstRows,
          numberOfColumnsFound,
          customerColumn,
          hasHeader,
          containerNumberColumn,
        } = response;

        if (firstRows.length >= 1) {
          for (let i = 0; i < numberOfColumnsFound; i += 1) {
            const name = hasHeader ? firstRows[0][i] : '-';
            const preview = firstRows[hasHeader ? 1 : 0][i];
            let mapping: BlueCargoProperty | null = null;

            if (containerNumberColumn !== null && containerNumberColumn === i) {
              mapping = 'container';
            } else if (customerColumn !== null && customerColumn === i) {
              mapping = 'customer';
            }

            const row: UploadedFileColumn = {
              id: i.toString(),
              name,
              preview,
              mapping,
            };
            columns.push(row);
          }
        }

        setState({
          stage: 'uploading',
          uploadId: response.containerTransactionWatchImportId,
          columns,
          fileName: file.name,
          success: false,
        });
      } catch (e) {
        setState({ stage: 'error', originStage: 'uploading' });
      }
    },
    [state, setState, method]
  );

  /** Assigns a customer tag or the column to the last upload id */
  const assignCustomer = React.useCallback(async () => {
    if (!uploadId || state.stage !== 'assignment') {
      return;
    }

    const selectedCustomerId =
      (state.assignment.kind === 'dropdown' && state.assignment.customer?.id) || null;
    let selectedColumnIndex: number | null = null;
    if (state.assignment.kind === 'column') {
      const index = state.assignment.columns.findIndex((c) => c.mapping === 'customer');
      if (index >= 0) {
        selectedColumnIndex = index;
      }
    }

    if (selectedCustomerId == null && selectedColumnIndex === null) {
      setState({ stage: 'complete' });
      return;
    }

    try {
      await assignCustomersToUpload({
        containerTransactionWatchImportId: uploadId,
        selectedCustomerId,
        selectedColumnIndex,
      });
      queryClient.invalidateQueries(['customers']);
      setState({ stage: 'complete' });
    } catch (error) {
      setState({ stage: 'error', originStage: 'assignment' });
    }
  }, [state, uploadId, queryClient]);

  const handleOnTrackAndTraceClick = React.useCallback(async () => {
    switch (state.stage) {
      case 'start': {
        if (method === 'input') {
          await uploadInputContainerNumbers(state.inputContainerNumbers);
        } else if (method === 'upload') {
          await uploadFileContainerNumbers(state.acceptedFiles);
        }
        break;
      }
      case 'assignment':
        await assignCustomer();
        break;
      default:
        break;
    }
  }, [state, method, uploadInputContainerNumbers, uploadFileContainerNumbers, assignCustomer]);

  const handleSetFiles = React.useCallback(
    (files: File[]) => {
      setState((s) => ({
        ...s,
        acceptedFiles: files,
      }));
    },
    [setState]
  );

  const handleSetNumbers = React.useCallback(
    (numbers: string[]) => {
      setState((s) => ({ ...s, inputContainerNumbers: numbers, acceptedFiles: [] }));
    },
    [setState]
  );

  React.useEffect(() => {
    if (state.stage === 'start' && state.acceptedFiles.length > 0) {
      handleOnTrackAndTraceClick();
    }
  }, [state, handleOnTrackAndTraceClick]);

  const shouldGetUploadStatus = !!(
    uploadId &&
    (state.stage === 'uploading' || state.stage === 'assignment') &&
    !state.success
  );

  useQuery(['getUploadStatus', uploadId], () => getUploadStatus(uploadId), {
    refetchInterval: shouldGetUploadStatus ? 2000 : false,
    enabled: shouldGetUploadStatus,
    onSuccess: (data) => {
      // Keep in mind that we only pull when there is an uploadId id and success is fals,
      // which only occurs in the uploading and assignment stages.
      setState((s) => {
        switch (data.status) {
          case 'duplicate':
            return { stage: 'duplicate' };
          case 'error':
            return { stage: 'error', originStage: 'uploading', message: data.message };
          case 'in_progress':
          case 'success':
          case 'pending':
          case 'retrying': {
            // If we're in uploading, we want to proceed to customer assignment
            if (s.stage === 'uploading') {
              return {
                stage: 'assignment',
                columns: s.columns,
                assignment: { kind: 'none' },
                uploadId: s.uploadId || '',
                fileName: s.fileName || '',
                success: data.status === 'success',
              };
            }

            // If we're in assignment, we want to mark success if that's the case so we stop polling
            if (s.stage === 'assignment' && data.status === 'success') {
              return { ...s, success: true };
            }

            return s;
          }
          default:
            return s;
        }
      });
    },
    onError: () => {
      setState({ stage: 'error', originStage: 'uploading' });
    },
  });

  const onAssignment = React.useCallback(
    (assignment: Assignment) => setState((s) => ({ ...s, assignment })),
    [setState]
  );

  let content: React.ReactNode = null;
  let buttonIsDisabled = false;
  switch (state.stage) {
    case 'start':
      content = (
        <Containers
          setFiles={handleSetFiles}
          setNumbers={handleSetNumbers}
          prepopulatedContainers={prepopulatedContainers || []}
          method={method}
        />
      );
      buttonIsDisabled = method === null || state.uploading;
      break;
    case 'uploading':
      content = <Uploading />;
      break;
    case 'assignment':
      content = (
        <CustomerAssignment
          columns={state.columns}
          onAssignment={onAssignment}
          fileName={state.fileName}
        />
      );
      break;
    case 'complete':
      content = <Success />;
      break;
    case 'duplicate':
      content = <Duplicate />;
      break;
    case 'error':
      content = (
        <Error
          onRetry={handleOnRetryUpload}
          originStage={state.originStage}
          message={state.message}
        />
      );
      break;
    default:
      break;
  }

  let footerContent: React.ReactNode = null;
  switch (state.stage) {
    case 'start':
    case 'assignment':
      footerContent = (
        <LoadingButton
          variant="contained"
          color="secondary"
          disabled={buttonIsDisabled}
          onClick={handleOnTrackAndTraceClick}
          size="large"
          type="submit"
          loading={state.stage === 'start' && state.uploading}
        >
          <h4>Track &amp; trace containers</h4>
        </LoadingButton>
      );
      break;
    case 'error':
    case 'duplicate':
    case 'complete':
      footerContent = (
        <Button
          onClick={() => onTrackAndTrace({ method: 'upload' })}
          variant="contained"
          size="large"
        >
          Close
        </Button>
      );
      break;
    default:
      break;
  }

  return (
    // eslint-disable-next-line
    // @ts-ignore
    <Card variant="modal" className="flex flex-col w-full pt-8 pb-4">
      <div className="space-y-2">
        <h2 className="mx-8 font-medium text-dark">Add containers to track</h2>
        <ContainerUsage className="px-8 pb-2 w-2/3" variant="simple" />
        <Divider className="border-blue-300 shadow-sm" />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleOnTrackAndTraceClick();
        }}
      >
        <div className="max-h-72 overflow-auto py-4">{content}</div>
        {footerContent && (
          <div className="space-y-2">
            <Divider className="border-blue-300" />
            <div className="flex justify-end px-8 pt-4">{footerContent}</div>
          </div>
        )}
      </form>
    </Card>
  );
};
AddForm.displayName = 'TrackAndTrace.AddContainers.Form';
